import { Box, Button, Center, Group, Loader, Stack, Text } from '@mantine/core';
import {
    ATTRACTION_MAP_LIMIT,
    AttractionMapBounds,
    AttractionMapPoint,
} from '@routellers/types/dist/city-attraction/attraction-map';
import maplibregl from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { getRoutellersMapStyle } from '../utils/app-map-style';

import 'maplibre-gl/dist/maplibre-gl.css';

type Props = {
    cityId: number | null;
    where: Record<string, unknown>;
    searchText: string;
    revision: number;
    onOpen: (id: number) => void;
};

const SOURCE = 'attraction-catalog-points';
const empty = () => ({ type: 'FeatureCollection' as const, features: [] });
const wrapLongitude = (lng: number) => ((((lng + 180) % 360) + 360) % 360) - 180;

const viewportBounds = (map: maplibregl.Map): AttractionMapBounds => {
    const bounds = map.getBounds();
    const wholeWorld = bounds.getEast() - bounds.getWest() >= 360;
    return {
        west: wholeWorld ? -180 : wrapLongitude(bounds.getWest()),
        east: wholeWorld ? 180 : wrapLongitude(bounds.getEast()),
        south: Math.max(-90, bounds.getSouth()),
        north: Math.min(90, bounds.getNorth()),
    };
};

export const AttractionsMap = ({ cityId, where, searchText, revision, onOpen }: Props) => {
    const { t, i18n } = useTranslation();
    const container = useRef<HTMLDivElement>(null);
    const open = useRef(onOpen);
    open.current = onOpen;
    const [map, setMap] = useState<maplibregl.Map | null>(null);
    const [loading, setLoading] = useState(true);
    const [count, setCount] = useState(0);
    const [limited, setLimited] = useState(false);
    const [error, setError] = useState('');
    const [retry, setRetry] = useState(0);

    useEffect(() => {
        void retry;
        let disposed = false;
        let instance: maplibregl.Map | undefined;
        let resize: ResizeObserver | undefined;
        const popup = new maplibregl.Popup({ closeButton: false, closeOnClick: false });
        setMap(null);
        setLoading(true);
        setError('');

        const init = async () => {
            const style = await getRoutellersMapStyle(i18n.language);
            if (disposed || !container.current) return;
            instance = new maplibregl.Map({
                container: container.current,
                style: style as maplibregl.StyleSpecification,
                center: [40, 35],
                zoom: 2,
                maxZoom: 19,
                // One visible world also avoids duplicate copies of each point.
                renderWorldCopies: false,
            });
            const current = instance;
            current.addControl(new maplibregl.NavigationControl(), 'top-right');
            resize = new ResizeObserver(() => current.resize());
            resize.observe(container.current);
            current.on('error', (event) => {
                if (!disposed && !current.isStyleLoaded()) {
                    setError(apiErrorMessage(event.error));
                    setLoading(false);
                }
            });
            current.on('load', () => {
                if (disposed) return;
                current.addSource(SOURCE, { type: 'geojson', data: empty() });
                current.addLayer({
                    id: SOURCE,
                    type: 'circle',
                    source: SOURCE,
                    paint: {
                        'circle-radius': 7,
                        'circle-color': '#228be6',
                        'circle-stroke-width': 2,
                        'circle-stroke-color': '#ffffff',
                    },
                });
                setMap(current);
            });
            current.on('click', SOURCE, (event) => {
                const id = Number(event.features?.[0]?.properties?.id);
                if (Number.isInteger(id) && id > 0) open.current(id);
            });
            current.on('mouseenter', SOURCE, (event) => {
                current.getCanvas().style.cursor = 'pointer';
                const feature = event.features?.[0];
                if (feature?.geometry.type !== 'Point') return;
                popup
                    .setLngLat(feature.geometry.coordinates as [number, number])
                    .setText(String(feature.properties.title))
                    .addTo(current);
            });
            current.on('movestart', () => popup.remove());
            current.on('mouseleave', SOURCE, () => {
                current.getCanvas().style.cursor = '';
                popup.remove();
            });
        };
        init().catch((err) => {
            if (!disposed) {
                setError(apiErrorMessage(err));
                setLoading(false);
            }
        });
        return () => {
            disposed = true;
            resize?.disconnect();
            popup.remove();
            instance?.remove();
        };
    }, [i18n.language, retry]);

    useEffect(() => {
        if (!map || !cityId) return;
        let disposed = false;
        api.cities
            .getOne(cityId)
            .then(({ data }) => {
                if (!disposed && data?.coordinates?.every(Number.isFinite)) {
                    map.jumpTo({ center: data.coordinates, zoom: 12 });
                }
            })
            .catch((err) => {
                if (!disposed) setError(apiErrorMessage(err));
            });
        return () => {
            disposed = true;
        };
    }, [map, cityId]);

    useEffect(() => {
        if (!map) return;
        void revision;
        const source = map.getSource(SOURCE) as maplibregl.GeoJSONSource;
        let timer: ReturnType<typeof setTimeout>;
        let controller: AbortController | undefined;
        let generation = 0;
        const clear = () => {
            generation += 1;
            clearTimeout(timer);
            controller?.abort();
            source.setData(empty());
            setCount(0);
            setLimited(false);
            setLoading(true);
        };
        const load = async () => {
            const current = ++generation;
            controller = new AbortController();
            setError('');
            try {
                const { data } = await api.cityAttractionsApi.mapPoints({
                    ...viewportBounds(map),
                    where,
                    searchText: searchText || undefined,
                    signal: controller.signal,
                });
                if (current !== generation || controller.signal.aborted) return;
                const bounds = viewportBounds(map);
                const points = (data?.items || [])
                    .filter(
                        (point: AttractionMapPoint) =>
                            point.lat >= bounds.south &&
                            point.lat <= bounds.north &&
                            (bounds.west <= bounds.east
                                ? point.lng >= bounds.west && point.lng <= bounds.east
                                : point.lng >= bounds.west || point.lng <= bounds.east),
                    )
                    .slice(0, ATTRACTION_MAP_LIMIT);
                source.setData({
                    type: 'FeatureCollection',
                    features: points.map((point) => ({
                        type: 'Feature',
                        properties: { id: point.id, title: point.title },
                        geometry: { type: 'Point', coordinates: [point.lng, point.lat] },
                    })),
                });
                setCount(points.length);
                setLimited(!!data?.limitReached);
            } catch (err) {
                if (current === generation && !controller.signal.aborted)
                    setError(apiErrorMessage(err));
            } finally {
                if (current === generation) setLoading(false);
            }
        };
        const schedule = () => {
            clear();
            timer = setTimeout(load, 250);
        };
        map.on('movestart', clear);
        map.on('moveend', schedule);
        schedule();
        return () => {
            generation += 1;
            clearTimeout(timer);
            controller?.abort();
            map.off('movestart', clear);
            map.off('moveend', schedule);
        };
    }, [map, where, searchText, revision]);

    return (
        <Stack gap='xs'>
            <Group gap='xs' aria-live='polite'>
                {loading && <Loader size='xs' />}
                <Text size='sm' c='dimmed'>
                    {t('pages.attractions.mapCount', { count })}
                    {limited ? ` · ${t('pages.attractions.mapLimit')}` : ''}
                </Text>
            </Group>
            <Box pos='relative'>
                <Box
                    ref={container}
                    role='region'
                    aria-label={t('pages.attractions.mapView')}
                    style={{
                        height: 'max(420px, 65vh)',
                        borderRadius: 12,
                        overflow: 'hidden',
                        border: '1px solid var(--mantine-color-gray-3)',
                    }}
                />
                {!loading && !error && count === 0 && (
                    <Center pos='absolute' inset={0} style={{ pointerEvents: 'none' }}>
                        <Text bg='white' p='sm' size='sm'>
                            {t('pages.attractions.mapEmpty')}
                        </Text>
                    </Center>
                )}
            </Box>
            {error && (
                <Group>
                    <Text c='red' size='sm'>
                        {error}
                    </Text>
                    <Button variant='subtle' size='xs' onClick={() => setRetry((v) => v + 1)}>
                        {t('pages.attractions.mapRetry')}
                    </Button>
                </Group>
            )}
        </Stack>
    );
};
